import { useEffect, useCallback, useState, useRef, KeyboardEvent } from 'react';
import { useLocation } from 'react-router';
import { createPopper, Instance, Placement } from '@popperjs/core';
import {
  childHasFocus,
  getFocusableElements,
  isActiveElement,
  isOrContainsTarget,
  isToggleCollapsibleButton,
} from '../lib/DomUtils/utils';
import { UseCollapsible } from './types/useCollapsibleOverlay';
import { GlobalDocumentIds } from '../types/GlobalDocumentIds';
import { KeyEvent } from '../types/KeyEvent';
import { useHtmlValidId } from './useHtmlValidId';

/**
 * Provides the base building blocks for an a11y conform collapsible component.
 *
 * A collapsible consists of a toggle and content component.
 *
 * Requirements are:
 *  - toggle and content components need to set className based on `isOpen` state
 *  - both have correct aria attributes
 *  - content renders an empty element b/c aria-controls need to point to a valid id
 *  - the clickOutside eventHandler is registered on `isOpen` and removed otherwise
 *  - content stays opened when clicked inside
 *  - content closes when clicked outside or ESC is hit
 *  - ESC needs to stop propagating
 *  - focus is handled properly
 *  - content shall be scrollable via SPACE
 *
 * TODO: if isActiveElement(collapsibleRef) and user hits Space in a
 * scrollable child container, the container should be scrolled before the
 * browser scrolls to the end of the page. This should be achieved by setting
 * tabIndex="-1" on the first child element (if scrollable) instead of the
 * collapsible itself.
 */
export default function useCollapsibleOverlay<TCollapsible extends HTMLElement = HTMLDivElement>(
  placement: Placement | null = null
): UseCollapsible<TCollapsible> {
  const toggleRef = useRef<HTMLButtonElement>(null);
  const collapsibleRef = useRef<TCollapsible>(null);
  const lastKeyPressed = useRef<{ code: string; shiftKey: boolean }>(undefined);
  const focusToFirstElementOnOpenByKeyboard = useRef(false);

  const [isOpen, setIsOpen] = useState(false);
  // True once we show the popover ourselves. While false, the native popover toggle is
  // prevented in `beforetoggle` so the popover is not shown before popper can align it.
  const popperReadyRef = useRef(false);

  const toggleCollapsible = useCallback(() => setIsOpen(state => !state), []);
  const closeCollapsible = useCallback(() => {
    toggleRef.current?.focus();
    setIsOpen(state => (state ? false : state));
  }, []);
  const openCollapsible = useCallback(() => setIsOpen(state => state || true), []);

  const location = useLocation();
  const id = useHtmlValidId();

  const focusToggle = (): void => {
    toggleRef.current?.focus();
  };

  const popperInstance = useRef<Instance>(undefined);

  /**
   * Closes the collapsible when user hits (Shift+) Tab or ESC.
   *
   * NOTE that the browsers default event flow goes like this:
   * 1. onKeyDown
   * 2. focus is set to next focusable element
   * 3. onKeyUp on the newly focused element is triggered
   *
   * Hence, when the user cycles through elements we must use the keydown event
   * to tackle (Shift+) Tab. We have to cover these cases:
   *
   * 1. Tab
   * - the last focusable element inside the collapsible has focus or
   * - the toggle component does not have focus (in case the collapsible does
   *   not have focusable elements)
   *
   * 2. Shift + Tab
   * - the toggle has focus
   */
  const onKeyDown = useCallback(
    (event: KeyboardEvent<any>): void => {
      lastKeyPressed.current = { code: event.code, shiftKey: event.shiftKey };

      if (
        isOpen &&
        (event.code === KeyEvent.Tab ||
          event.code === KeyEvent.Escape ||
          event.code === KeyEvent.ArrowDown ||
          event.code === KeyEvent.ArrowUp ||
          event.code === 'Down' ||
          event.code === 'Up')
      ) {
        if (collapsibleRef.current) {
          const focusableElements = getFocusableElements(collapsibleRef.current);

          if (event.code === KeyEvent.Tab && focusableElements.length === 0) {
            // case for help text element
            setIsOpen(false);
          } else if (event.code === KeyEvent.Escape) {
            // prevent default to avoid for example modal closing on escape
            event.preventDefault();
            event.stopPropagation();

            toggleRef.current?.focus();
            setIsOpen(false);
          } else if (
            event.code === KeyEvent.Tab ||
            event.code === KeyEvent.ArrowDown ||
            event.code === KeyEvent.ArrowUp
          ) {
            // lookup if focus is already in collapsible or not
            const focusedElementIndex = focusableElements?.findIndex(el => el === document.activeElement) ?? -1;

            if (
              focusedElementIndex === 0 &&
              ((event.shiftKey && event.code === KeyEvent.Tab) || (!event.shiftKey && event.code === KeyEvent.ArrowUp))
            ) {
              event.preventDefault();
              event.stopPropagation();

              focusableElements.at(focusableElements.length - 1)?.focus();
            } else if (
              focusedElementIndex === focusableElements.length - 1 &&
              !event.shiftKey &&
              (event.code === KeyEvent.Tab || event.code === KeyEvent.ArrowDown)
            ) {
              event.preventDefault();
              event.stopPropagation();

              focusableElements.at(0)?.focus();
            } else if (
              event.code === KeyEvent.ArrowDown ||
              event.code === KeyEvent.ArrowUp ||
              event.code === 'Down' ||
              event.code === 'Up'
            ) {
              event.preventDefault();
              event.stopPropagation();

              const nextFocus =
                event.code === KeyEvent.ArrowDown || event.code === 'Down'
                  ? focusedElementIndex + 1
                  : focusedElementIndex - 1;

              focusableElements?.at(nextFocus)?.focus();
            }
          }
        }
      } else if (!isOpen && (event.code === KeyEvent.Enter || event.code === KeyEvent.Space)) {
        focusToFirstElementOnOpenByKeyboard.current = true;
      }
    },
    [isOpen]
  );

  useEffect(() => {
    if (isOpen && focusToFirstElementOnOpenByKeyboard.current && collapsibleRef.current) {
      focusToFirstElementOnOpenByKeyboard.current = false;
      getFocusableElements(collapsibleRef.current).at(0)?.focus();
    }
  }, [isOpen]);

  const onOutsideClick = (event: MouseEvent) => {
    // click is on the toggling button -> don't close, onclick from button will handle this
    // click inside container -> don't close, valid click
    // When the popover is detached to <body> (e.g. inside a modal dialog), the parent-based
    // check (isToggleCollapsibleButton) fails because the collapsible and toggle no longer share parents.
    // Fall back to checking the toggle ref directly.
    if (
      isActiveElement(collapsibleRef) ||
      isOrContainsTarget(collapsibleRef, event.target) ||
      isToggleCollapsibleButton(collapsibleRef, event) ||
      isOrContainsTarget(toggleRef, event.target)
    ) {
      return;
    }

    if (!isActiveElement(collapsibleRef) && !childHasFocus(collapsibleRef)) {
      setIsOpen(false);
    }
  };

  /**
   * For the collapsibles in ModalDialog, we need to add EventListener in modal-dialog node
   * as outside clickable node because modal-dialog is outside root node.
   * */
  useEffect(() => {
    let outsideClickNode = document.getElementById(GlobalDocumentIds.modalDialog);
    if (!outsideClickNode) {
      outsideClickNode = document.getElementById(GlobalDocumentIds.root);
    }

    if (isOpen && outsideClickNode) {
      outsideClickNode.addEventListener('click', onOutsideClick);
    }

    return () => {
      if (outsideClickNode) {
        outsideClickNode.removeEventListener('click', onOutsideClick);
      }
    };
  }, [isOpen, toggleCollapsible]);

  // Show and position the popover. Runs in a macrotask (setTimeout 0): calling
  // showPopover() synchronously in the passive effect is too early – the browser has
  // not yet nested the popover into the top layer, so it fails silently and the
  // content stays in the normal layout flow.
  // Order within the callback:
  //   1. showPopover()   -> add to the top layer. Still hidden via the missing
  //      "--popper-ready" class, so it is never visible while being positioned.
  //   2. createPopper()  -> position it. Must run after showPopover(): until the
  //      element is in the top layer, popper measures the wrong containing block.
  //   3. "--popper-ready" -> reveal. Popper is intentionally NOT re-run afterwards
  //      (no `collapsibleRefHeight` dependency) so it never repositions a visible
  //      popover -> no flicker, no wrong alignment.
  useEffect(() => {
    const el = collapsibleRef.current;
    if (!isOpen || !el) {
      return;
    }

    const timer = setTimeout(() => {
      // Allow our own showPopover() (the native toggle is prevented until now).
      if (placement) {
        popperReadyRef.current = true;
      }

      if (typeof el.showPopover === 'function') {
        try {
          el.showPopover();
        } catch {
          // already showing – ignore
        }
      }

      if (placement && toggleRef.current) {
        popperInstance.current?.destroy();
        popperInstance.current = createPopper(toggleRef.current, el, {
          placement,
          modifiers: [
            {
              name: 'preventOverflow',
              enabled: false,
            },
            {
              name: 'offset',
              options: {
                offset: ({ placement: popperPlacement }: { placement: Placement }) => {
                  if (!toggleRef.current) {
                    return [0, 0];
                  }
                  const halfWidth = toggleRef.current.offsetWidth / 2;
                  if (popperPlacement === 'bottom-start' || popperPlacement === 'top-start') {
                    return [-halfWidth, 0];
                  }
                  if (popperPlacement === 'bottom-end' || popperPlacement === 'top-end') {
                    return [halfWidth, 0];
                  }
                  return [0, 0];
                },
              },
            },
          ],
        });
      }

      el.classList.add('collapsible-overlay--popper-ready');
    }, 0);

    return () => {
      clearTimeout(timer);
      popperInstance.current?.destroy();
      popperInstance.current = undefined;
      popperReadyRef.current = false;
    };
  }, [isOpen, placement]);

  // Intercept the browser's automatic popover toggle so we can control visibility ourselves.
  // The popover is only shown after popper has positioned it.
  useEffect(() => {
    const el = collapsibleRef.current;
    if (!el) {
      return;
    }

    const handleBeforeToggle = (event: Event) => {
      const toggleEvent = event as unknown as { newState: string };
      if (toggleEvent.newState === 'open') {
        // For popper-positioned collapsibles, never let the browser show the popover
        // before popper has aligned it. We call showPopover() ourselves (in the show
        // effect) instead.
        if (placement && !popperReadyRef.current) {
          event.preventDefault();
        }
        return;
      }
      // Closing: defer hidePopover to avoid triggering it synchronously within the beforetoggle event.
      // Calling hidePopover() during beforetoggle while another popover is opening causes a browser warning.
      setTimeout(() => {
        el.classList.remove('collapsible-overlay--popper-ready');
        el.hidePopover?.();
      }, 0);
    };

    el.addEventListener('beforetoggle', handleBeforeToggle);
    return () => el.removeEventListener('beforetoggle', handleBeforeToggle);
  }, [placement]);

  // When closing via state change (e.g., ESC, outside click), hide the popover.
  useEffect(() => {
    if (!isOpen && collapsibleRef.current) {
      collapsibleRef.current.classList.remove('collapsible-overlay--popper-ready');
      collapsibleRef.current.hidePopover?.();
    }
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.key]);

  // Default props for a toggle component with correct aria attributes
  const toggleProps = {
    'aria-controls': id,
    popoverTarget: id,
    popoverTargetAction: 'toggle' as const,
    onClick: toggleCollapsible,
    onKeyDown,
    ref: toggleRef,
  };

  // Default props for a (i.e.) <CollapsibleContent /> component
  // which renders its children based on `isOpen`.
  // Optionally it takes a `className`.
  const collapsibleProps = {
    'aria-hidden': !isOpen,
    popover: 'manual' as const,
    id,
    isOpen,
    onKeyDown,
    ref: collapsibleRef,
    tabIndex: -1,
  };

  return {
    closeCollapsible,
    openCollapsible,
    collapsibleOpen: isOpen,
    toggleCollapsible,
    focusToggle,
    toggleProps,
    collapsibleProps,
    popperInstance: popperInstance.current,
  };
}
